<script>
  import Cell from './cell.svelte';
  import { onMount } from 'svelte';

  export let slide;
  export { className as class };

  export let progress = 0;

  let className = '';

  // helper to fade the opacity of some elements
  let fadeInOpacity;
  let fadeOutOpacity;

  // Helpers for setting the animated squares size
  let initialSize;
  let targetSize;
  let currentSize;

  // array containing all possible sizes for the squares
  // including the breakpoint on which them should be set
  // the order is important, do not change it
  const widths = [
    {
      start: 216,
      end: 280,
      break: 0,
    },
    {
      start: 264,
      end: 358,
      break: 595,
    },
    {
      start: 288,
      end: 376,
      break: 714,
    },
    {
      start: 336,
      end: 432,
      break: 1024,
    },
    {
      start: 360,
      end: 466,
      break: 1280,
    },
    {
      start: 464,
      end: 612,
      break: 1601,
    },
  ].reverse();

  onMount(() => {
    updateValues();

    window.addEventListener('resize', updateValues);
  });

  $: {
    fadeOutOpacity = Math.pow(1 - progress, 2);
    fadeInOpacity = 1 - fadeOutOpacity;

    currentSize = slide == 0 ? (targetSize - initialSize) * progress + initialSize : targetSize;
  }

  /**
   * Update values depending on the viewport size
   */
  const updateValues = () => {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

    // get in which breakpoint the user layout is
    const newViewport = widths.find((view) => vw >= view.break);

    initialSize = newViewport.start;
    targetSize = newViewport.end;

    // determine the current size based on the progress
    currentSize = slide == 0 ? (targetSize - initialSize) * progress + initialSize : targetSize;
  };

  // each subarray on squares array represents one state (or slide) of animated component
  // to add or edit a subarray keep in mind that it must have 16 objects
  // to easily understand objects order, they are: 1-4 / 5-8 / 9-12 / 13-16
  // accepted color values are: red, yellow, blue-dark, blue-light, orange, purple, white and transparent
  // if adding another color is needed, it must be also added to cell component as conditional or tailwind won't be able to load it
  // accepted borderRadius values are any % from 0 to 100
  // transitionOpacity is used to choose when we should animate the opacity of a square based on the scroll;

  let squares = [
    [
      { color: 'red', borderRadius: '0%' },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'red', borderRadius: '0%' },
      { color: 'blue-dark', borderRadius: '0%' },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%' },
      { color: 'blue-dark', borderRadius: '0%' },
      { color: 'red', borderRadius: '0%' },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%' },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'orange', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%' },
    ],
    [
      { color: 'red', borderRadius: '0%', opacity: 0, fadeOut: true },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'red', borderRadius: '0%' },
      { color: 'blue-dark', borderRadius: '0%', opacity: 0, fadeOut: true },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%' },
      { color: 'blue-dark', borderRadius: '0%' },
      { color: 'red', borderRadius: '0%' },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'blue-light', borderRadius: '0%', opacity: 0, fadeOut: true },
      { color: 'yellow', borderRadius: '0%' },
      { color: 'orange', borderRadius: '0%' },
      { color: 'white', borderRadius: '0%', opacity: 0, fadeOut: true },
    ],
    [
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
    ],
    [
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'blue-dark', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
    ],
    [
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
      { color: 'purple', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'white', borderRadius: '50%', opacity: 0, fadeIn: true },
      { color: 'purple', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'red', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'yellow', borderRadius: '50%', opacity: 0, fadeIn: true },
      { color: 'purple', borderRadius: '50%' },
      { color: 'purple', borderRadius: '50%' },
      { color: 'transparent', borderRadius: '50%', opacity: 0 },
    ],
    [
      { color: 'transparent', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'white', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'yellow', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'purple', borderRadius: '50%', opacity: 0, fadeOut: true },
      { color: 'transparent', borderRadius: '50%', opacity: 0, fadeOut: true },
    ],
  ];

  // sets the nextColor field so we can animated the colour in-between steps
  squares = squares.map((state, index) => {
    return state.map((square, sqrIndex) => {
      const nextIndex = index == squares.length - 1 ? squares.length - 1 : index + 1;
      return { ...square, nextColor: squares[nextIndex][sqrIndex].color };
    });
  });
</script>

<div
  id="squares"
  class={`squares-container ${className} items-center`}
  style="width: {currentSize}px; height: {currentSize}px"
>
  {#each squares[slide] as square}
    <!-- Slide 3 is the only side that the border radius changes -->
    <Cell
      color={progress > 0.5 ? square.nextColor : square.color}
      borderRadius={slide === 2 ? progress * 50 : slide > 2 ? 100 : 0}
      opacity={square.fadeIn ? fadeInOpacity : square.fadeOut ? fadeOutOpacity : square.opacity}
    />
  {/each}
</div>

<style lang="postcss">
  .squares-container {
    transition: width 25ms ease;
    @apply grid grid-cols-4 grid-rows-4;
  }
</style>
